import { Transport, TransportRequest, TransportResponse } from "../../../transport/Transport"
import {
  EntityEvaluateRequestDto,
  EntityEvaluateResponseDto,
  WorkspaceEvaluateRequestDto,
  WorkspaceEvaluateResponseDto
} from "../model/dto"
import RemoteEvaluateClient from "./RemoteEvaluateClient"
import { ApiCallMetrics, ApiOperation } from "../../../metrics/monitoring/ApiCallMetrics"

const NO_CONTENT = 204

/**
 * XHR-backed remote evaluation client. The transport is expected to be intercepted with the SDK
 * queries. The workspace endpoint returns HTTP 204 for not-modified, resolved here as `null`.
 */
export class HttpRemoteEvaluateClient implements RemoteEvaluateClient {
  constructor(
    private readonly workspaceUrl: string,
    private readonly entityUrl: string,
    private readonly transport: Transport
  ) {}

  async evaluateIfModified(request: WorkspaceEvaluateRequestDto): Promise<WorkspaceEvaluateResponseDto | null> {
    const transportRequest = this.createRequest(this.workspaceUrl, request)
    const response = await this.execute(transportRequest, "evaluate.workspace")
    return this.handleWorkspaceResponse(response)
  }

  async evaluateEntities(request: EntityEvaluateRequestDto): Promise<EntityEvaluateResponseDto> {
    const transportRequest = this.createRequest(this.entityUrl, request)
    const response = await this.execute(transportRequest, "evaluate.entities")
    return this.handleResponse<EntityEvaluateResponseDto>(response)
  }

  private createRequest(url: string, body: unknown): TransportRequest {
    return TransportRequest.builder()
      .url(url)
      .method("POST")
      .addHeader("Content-Type", "text/plain;charset=UTF-8") // text/plain body to avoid CORS preflight
      .body(JSON.stringify(body))
      .build()
  }

  private execute(request: TransportRequest, operation: ApiOperation): Promise<TransportResponse> {
    return ApiCallMetrics.record(operation, () => this.transport.send(request))
  }

  private handleWorkspaceResponse(response: TransportResponse): WorkspaceEvaluateResponseDto | null {
    if (response.statusCode === NO_CONTENT) {
      return null
    }
    return this.handleResponse<WorkspaceEvaluateResponseDto>(response)
  }

  private handleResponse<T>(response: TransportResponse): T {
    if (!response.isSuccessful()) {
      throw new Error(`Http status code: ${response.statusCode}`)
    }
    if (response.body === null) {
      throw new Error("Response body is empty")
    }
    return JSON.parse(response.body) as T
  }
}
